import React, { useMemo, useState } from 'react';
import { AlertTriangle, Loader2, ShieldCheck } from 'lucide-react';
import { bulkRemoveControlEvidence, type BulkRemoveResult } from '../../../request/evidence-library/evidenceLibraryRequests.ts';
import { toastLibraryError, useInvalidateLibrary } from '../../../hooks/evidence-library/useEvidenceLibrary.ts';
import type { ControlEvidenceRow, EvidenceUsage } from './EvidenceFolderTable.tsx';
import { LibraryDialogShell, primaryBtn, secondaryBtn } from './LibraryDialogShell.tsx';

// Same list as the backend (evidence_remove_handlers.go finishedRunStatuses): after these a run no longer reads the
// control's evidence. failed / cancelled / timed_out runs are re-run in place and in_review is sent back for rework.
const FINISHED = new Set(['completed', 'prepared', 'signed_off']);

// Same limit as the backend (maxBulkRemove).
const MAX_REMOVE = 500;

const RESULT_TEXT: Record<string, string> = {
  unlinked: 'Removed from this control',
  deleted: 'Deleted from the library',
  deleted_retained: 'Deleted from the library, kept for completed testing',
  kept_shared: 'Removed from this control, kept for other controls',
  blocked: 'Not removed',
  error: 'Failed',
};

/**
 * Confirms removing one file, many files or a folder from a control: unlink only, or also delete from the library.
 * Says up front which files completed testing used (kept for those runs) and which an unfinished run still needs
 * (skipped).
 */
export function RemoveEvidenceDialog({
  controlUuid,
  files,
  usage,
  title,
  onClose,
  onDone,
}: {
  controlUuid: string;
  files: ControlEvidenceRow[];
  usage: Record<string, EvidenceUsage>;
  title: string;
  onClose: () => void;
  onDone: () => void;
}) {
  const [mode, setMode] = useState<'unlink' | 'delete'>('unlink');
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState<BulkRemoveResult[] | null>(null);
  const invalidate = useInvalidateLibrary();

  const uploads = useMemo(() => Array.from(new Set(files.map((f) => f.control_upload_id))), [files]);
  // A file linked twice (two periods) is one file to the user: list it once.
  const listed = useMemo(() => files.filter((f, i) => files.findIndex((g) => g.control_upload_id === f.control_upload_id) === i), [files]);
  const tooMany = files.length > MAX_REMOVE;
  const stats = useMemo(() => {
    let usedInTesting = 0;
    let inOpenRun = 0;
    let shared = 0;
    uploads.forEach((id) => {
      const u = usage[String(id)];
      const runs = u?.runs ?? [];
      if (runs.some((r) => !FINISHED.has(r.workflow_status))) inOpenRun += 1;
      else if (runs.length) usedInTesting += 1;
      if (u?.other_controls?.length) shared += 1;
    });
    return { usedInTesting, inOpenRun, shared };
  }, [uploads, usage]);

  const confirm = async () => {
    setBusy(true);
    try {
      const res = await bulkRemoveControlEvidence(controlUuid, files.map((f) => f.id), mode === 'delete');
      setResults(res);
      invalidate();
    } catch (e) {
      toastLibraryError(e, 'Could not remove the files');
    } finally {
      setBusy(false);
    }
  };

  if (results) {
    const counts = results.reduce<Record<string, number>>((acc, r) => ({ ...acc, [r.status]: (acc[r.status] ?? 0) + 1 }), {});
    const notable = results.filter((r) => r.status === 'blocked' || r.status === 'kept_shared' || r.status === 'error');
    return (
      <LibraryDialogShell
        title={title}
        onClose={onDone}
        footer={<button type="button" className={primaryBtn} onClick={onDone}>Done</button>}
      >
        <ul className="space-y-1 text-sm">
          {Object.entries(counts).map(([status, n]) => (
            <li key={status} className={status === 'blocked' || status === 'error' ? 'text-amber-800' : 'text-gray-800'}>
              <b>{n}</b> {RESULT_TEXT[status] ?? status}
            </li>
          ))}
        </ul>
        {notable.length ? (
          <ul className="max-h-60 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 text-sm">
            {notable.map((r) => (
              <li key={r.control_upload_id} className="px-3 py-2">
                <p className="truncate font-medium text-gray-800">{r.filename}</p>
                <p className="text-xs text-gray-600">{r.detail}</p>
              </li>
            ))}
          </ul>
        ) : null}
      </LibraryDialogShell>
    );
  }

  const n = uploads.length;
  return (
    <LibraryDialogShell
      title={title}
      subtitle={`${n} file${n !== 1 ? 's' : ''} selected`}
      onClose={onClose}
      closeDisabled={busy}
      footer={
        <>
          <button type="button" className={secondaryBtn} onClick={onClose} disabled={busy}>Cancel</button>
          <button
            type="button"
            className={mode === 'delete' ? 'rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700 disabled:opacity-50' : primaryBtn}
            onClick={confirm}
            disabled={busy || n === 0 || tooMany}
          >
            {busy ? <Loader2 className="mr-1 inline h-4 w-4 animate-spin" /> : null}
            {mode === 'delete' ? `Delete ${n} file${n !== 1 ? 's' : ''}` : `Unlink ${n} file${n !== 1 ? 's' : ''}`}
          </button>
        </>
      }
    >
      <fieldset className="space-y-2">
        <label className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${mode === 'unlink' ? 'border-blue-300 bg-blue-50/50' : 'border-gray-200'}`}>
          <input type="radio" name="remove-mode" className="mt-1" checked={mode === 'unlink'} onChange={() => setMode('unlink')} />
          <span>
            <span className="block text-sm font-medium text-gray-900">Unlink from this control</span>
            <span className="block text-xs text-gray-600">The files leave this control's evidence store and stay in the evidence library, where you can add them again.</span>
          </span>
        </label>
        <label className={`flex cursor-pointer gap-3 rounded-lg border p-3 ${mode === 'delete' ? 'border-red-300 bg-red-50/50' : 'border-gray-200'}`}>
          <input type="radio" name="remove-mode" className="mt-1" checked={mode === 'delete'} onChange={() => setMode('delete')} />
          <span>
            <span className="block text-sm font-medium text-gray-900">Delete from the evidence library</span>
            <span className="block text-xs text-gray-600">Also removes the files from the library. Files another control uses are only unlinked from this one.</span>
          </span>
        </label>
      </fieldset>

      {tooMany ? (
        <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
          Remove at most {MAX_REMOVE} files at a time. Select fewer files, or remove a smaller folder.
        </p>
      ) : null}
      {stats.usedInTesting ? (
        <p className="flex items-start gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-800">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />
          {stats.usedInTesting} file{stats.usedInTesting !== 1 ? 's were' : ' was'} used in completed testing. Their results are not affected: each run keeps the exact file it tested{mode === 'delete' ? ', even after it is deleted from the library' : ''}.
        </p>
      ) : null}
      {stats.inOpenRun ? (
        <p className="flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {stats.inOpenRun} file{stats.inOpenRun !== 1 ? 's are' : ' is'} used by a run that has not finished and will be skipped. Remove {stats.inOpenRun !== 1 ? 'them' : 'it'} from that run first.
        </p>
      ) : null}
      {mode === 'delete' && stats.shared ? (
        <p className="text-xs text-gray-600">{stats.shared} file{stats.shared !== 1 ? 's are' : ' is'} also on another control and will stay in the library.</p>
      ) : null}

      <ul className="max-h-48 divide-y divide-gray-100 overflow-y-auto rounded-lg border border-gray-200 text-sm">
        {listed.map((f) => (
          <li key={f.id} className="truncate px-3 py-1.5 text-gray-700" title={f.relative_path || f.filename}>{f.relative_path || f.filename}</li>
        ))}
      </ul>
    </LibraryDialogShell>
  );
}
